import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  deletePalishReceipt,
  findAllPalishReceipt,
} from "./services/palishReceipt.services";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import {
  clearFilterPalishReceipt,
  clearPalishReceiptDetail,
  setFilterPalishReceipt,
  setPalishReceiptID,
} from "./palishReceiptSlice";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
  GridCustomFilterCellProps,
} from "@progress/kendo-react-grid";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import IconButton from "../../components/common/IconButton";
import { FiEdit } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { LoadingPanel } from "../../components/layout/Loading";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { checkAcessRights, formatToIndianCurrency } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
  PRINT_ACCESS,
} from "../../_contstants/common";
import CommonFilterCell from "../../components/common/GlobalCommonFilterCell";
import { FaEye } from "react-icons/fa";

const PalishReceipt: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);
  useEffect(() => {
    return () => {
      dispatch(closeDialog());
      dispatch(clearPalishReceiptDetail());
      dispatch(setCurrentPage(0));
    };
  }, []);

  const handleProcessCreate = () => {
    navigate("/palishreceipt/create");
  };
  return (
    <>
      <DeletePalishReceiptDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Palish Receipt List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleProcessCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              Add New
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <PalishReceiptGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const PalishReceiptGridComponent: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const PalishReceiptList = useAppSelector(
    (state) => state.palishreceipt.PalishReceiptList
  );
  const loading = useAppSelector((state) => state.palishreceipt.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);
  const FilterPalishReceipt = useAppSelector(
    (state) => state.palishreceipt.FilterPalishReceipt
  );
  useEffect(() => {
    const payload = {
      ...FilterPalishReceipt,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(findAllPalishReceipt(payload));
  }, [currentPage, pageLimit]);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const MyEditCommandCell = (props: any) => {
    const handleEditItem = (palish_receipt_guid: string) => {
      navigate("/palishreceipt/edit", {
        state: { palish_receipt_guid: palish_receipt_guid },
      });
    };

    const handlePrint = (ghat_guid: string) => {
      window.open(`/palishreceipt/${ghat_guid}`, "_blank");
    };

    const handleOpenDeleteDialog = (palish_receipt_guid: string) => {
      dispatch(openDialog("deletePalishReceipt"));
      dispatch(setPalishReceiptID(palish_receipt_guid));
    };

    return (
      <td
        {...props.tdProps}
        className={`action-td ${props.className}`}
        style={{ ...props.tdProps.style }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            themeColor="success"
            onClick={() => handleEditItem(props.dataItem.palish_receipt_guid)}
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            themeColor="error"
            onClick={() =>
              handleOpenDeleteDialog(props.dataItem.palish_receipt_guid)
            }
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}

        {isPrintAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="View"
            themeColor="tertiary"
            onClick={() => handlePrint(props.dataItem.palish_receipt_guid)}
          >
            <FaEye className="absolute-position" style={{ fontSize: "24px" }} />
          </IconButton>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 5 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );
  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const PalishReceiptFilterCell = React.useCallback(
    (props: GridCustomFilterCellProps) => (
      <CommonFilterCell
        {...props}
        filterSelector={(state) => state.palishreceipt.FilterPalishReceipt}
        setFilterAction={setFilterPalishReceipt}
        clearFilterAction={clearFilterPalishReceipt}
        fetchDataAction={findAllPalishReceipt}
        pagination={{ case: "lowercase" }}
      />
    ),
    []
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        scrollable="scrollable"
        className="responsive-table"
        style={{ maxHeight: "calc(100vh - 152px)" }}
        filterable={true}
        filter={filter}
        data={filter ? filterBy(PalishReceiptList, filter) : PalishReceiptList}
        total={PalishReceiptList[0]?.itemcount || PalishReceiptList.length}
        onFilterChange={handleFilterChange}
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          width={100}
          field="palish_receipt_no"
          title="Palish Receipt No"
          cells={{
            data: (props: any) => (
              <td>
                <div
                  className="hoverableName"
                  onClick={() =>
                    navigate("/palishreceipt/edit", {
                      state: {
                        palish_receipt_guid:
                          props.dataItem?.palish_receipt_guid,
                      },
                    })
                  }
                >
                  {props.dataItem?.palish_receipt_no}
                </div>
              </td>
            ),
          }}
        />
        <Column
          field="receipt_date"
          title="Receipt Date"
          width={120}
          cells={{ filterCell: PalishReceiptFilterCell }}
        />
        <Column
          field="karigar_name"
          title="Karigar Name"
          width={200}
          cells={{ filterCell: PalishReceiptFilterCell }}
        />
        <Column
          field="charges"
          title="Charges"
          width={200}
          cells={{
            data: (props: any) => (
              <td>
                <div>
                  <Typography.h6 style={{ margin: 0, textAlign: "end" }}>
                    {formatToIndianCurrency(props.dataItem?.charges)}
                  </Typography.h6>
                </div>
              </td>
            ),
          }}
          filterable={false}
        />
        <Column
          field="gold_rate"
          title="Gold Rate"
          width={200}
          filterable={false}
          cells={{
            data: (props: any) => (
              <td>
                <div>
                  <Typography.h6 style={{ margin: 0, textAlign: "end" }}>
                    {formatToIndianCurrency(props.dataItem?.gold_rate)}
                  </Typography.h6>
                </div>
              </td>
            ),
          }}
        />
        {(isEditAccess || isDeleteAccess) && (
          <Column
            field="palish_guid"
            title="Actions"
            cells={{ data: MyEditCommandCell }}
            width={"150px"}
            filterable={false}
            locked={true}
          />
        )}
      </KendoGrid>
    </>
  );
};

const DeletePalishReceiptDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const PalishReceiptID = useAppSelector(
    (state) => state.palishreceipt.PalishReceiptID
  );
  const FilterPalishReceipt = useAppSelector(
    (state) => state.palishreceipt.FilterPalishReceipt
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);

  const handleDeleteAction = async (ID: number | null) => {
    const getpayload = {
      ...FilterPalishReceipt,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    if (ID) {
      const response = await dispatch(deletePalishReceipt(ID));
      dispatch(setCurrentPage(0));
      if (response.payload?.Data.Status === 200) {
        dispatch(findAllPalishReceipt(getpayload));
        dispatch(closeDialog());
        dispatch(clearPalishReceiptDetail());
      } else {
        dispatch(findAllPalishReceipt(getpayload));
        dispatch(closeDialog());
        dispatch(clearPalishReceiptDetail());
      }
    }
  };

  return (
    <>
      {dialogName === "deletePalishReceipt" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Palish Receipt?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Palish Receipt?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => {
                    handleDeleteAction(PalishReceiptID);
                  }}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default PalishReceipt;
